import {useState} from 'react';
import styled from 'styled-components';
import {PI_CARDINAL, PI_FILL} from '../pi/piPaths';
import {Pulse} from '../mascot/plushKit';
import {CANDY, FELT, FeltDefs, useFeltIds} from './felt';
import {Hop, ToyShell, type MapToyProps} from './mapToys';

/**
 * Map-sized "AI toys", companions to the probability toys in `mapToys.tsx`:
 * small felt pictures of AI ideas that play when clicked. Each follows the
 * same {@link ToyShell} contract — a real button whose status is in its
 * accessible name (nothing is written on the toy), or a static picture when
 * `isPreview`.
 */

/** Autocomplete candidates after "I love", with their probabilities. */
const AUTOCOMPLETE = [
  {word: 'cats', p: 0.45},
  {word: 'pizza', p: 0.3},
  {word: 'math', p: 0.15},
  {word: 'naps', p: 0.1},
] as const;

/** Scale that fits the π mark into the speech bubble. */
const BUBBLE_PI_SCALE = 0.2;

/** A drawing of an autocomplete word, centered in the bubble at (50, 29). */
function WordPicture({word}: {word: string}) {
  switch (word) {
    case 'cats':
      return (
        <g transform="translate(50 30) scale(0.72) translate(-50 -40)">
          <CatFace />
        </g>
      );
    case 'pizza':
      return (
        <g>
          <path d="M 35 14 L 65 14 L 50 46 Z" fill="#F2C14E" />
          <rect x={33} y={11} width={34} height={6} rx={3} fill="#D9A04A" />
          <circle cx={45} cy={22} r={3} fill={FELT.cardinal} />
          <circle cx={55} cy={24} r={2.6} fill={FELT.cardinal} />
          <circle cx={50} cy={33} r={2.4} fill={FELT.cardinal} />
        </g>
      );
    case 'math':
      return (
        <path
          d={PI_FILL}
          fill={PI_CARDINAL}
          transform={`translate(${50 - 109 * BUBBLE_PI_SCALE} ${29 - 94 * BUBBLE_PI_SCALE}) scale(${BUBBLE_PI_SCALE})`}
        />
      );
    default:
      return (
        <g>
          <path
            d="M 54 14 A 15 15 0 1 0 60 42 A 12 12 0 1 1 54 14 Z"
            fill={FELT.gold}
          />
          <circle cx={63} cy={17} r={1.8} fill={FELT.gold} />
          <circle cx={68} cy={25} r={1.2} fill={FELT.gold} />
        </g>
      );
  }
}

/**
 * A speech bubble that is typing: click to sample how "I love …" ends, and a
 * picture of the sampled word pops in.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyAutocomplete({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [picks, setPicks] = useState<number[]>([]);
  const latest = picks[picks.length - 1];
  const pick = latest === undefined ? undefined : AUTOCOMPLETE[latest];

  const sample = () => {
    let draw = Math.random();
    const index = AUTOCOMPLETE.findIndex(candidate => {
      draw -= candidate.p;
      return draw < 0;
    });
    setPicks([...picks, index === -1 ? 0 : index]);
  };

  return (
    <ToyShell
      isPreview={isPreview}
      label="Autocomplete"
      caption={
        pick === undefined
          ? 'finish my sentence'
          : `I love ${pick.word}, ${Math.round(pick.p * 100)}%`
      }
      onPoke={sample}
    >
      <svg viewBox="0 0 100 70" width={size} height={size * 0.7} aria-hidden>
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <path
          d="M 14 6 H 86 Q 96 6 96 16 V 42 Q 96 52 86 52 H 34 L 20 64 L 24 52 H 14 Q 4 52 4 42 V 16 Q 4 6 14 6 Z"
          fill="#fff"
          stroke={FELT.creamDark}
          strokeWidth={2.5}
          filter={`url(#${ids.fuzz})`}
        />
        {pick === undefined ? (
          [38, 50, 62].map((cx, index) => (
            <Pulse
              key={cx}
              cx={cx}
              cy={29}
              r={4.5}
              fill={FELT.creamDark}
              $delaySec={index * 0.2}
            />
          ))
        ) : (
          <Hop key={picks.length}>
            <WordPicture word={pick.word} />
          </Hop>
        )}
      </svg>
    </ToyShell>
  );
}

/** The neuron's input weights and firing threshold. */
const NEURON = {weights: [2, -1, 1.5], threshold: 1.5} as const;
const NEURON_INPUT_Y = [20, 45, 70] as const;

/**
 * A single neuron: three felt-button inputs on yarn feeding one big button.
 * Each click steps the inputs through every on/off pattern; the neuron
 * lights and sparks when the weighted sum reaches its threshold.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyNeuron({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [pattern, setPattern] = useState(5);
  const inputs = [4, 2, 1].map(bit => ((pattern & bit) === 0 ? 0 : 1));
  const sum = inputs.reduce(
    (total, input, index) => total + input * (NEURON.weights[index] ?? 0),
    0,
  );
  const fires = sum >= NEURON.threshold;

  return (
    <ToyShell
      isPreview={isPreview}
      label="Neuron"
      caption={fires ? `fires! (${sum})` : `quiet (${sum})`}
      onPoke={() => {
        setPattern((pattern + 1) % 8);
      }}
    >
      <svg viewBox="0 0 100 90" width={size} height={size * 0.9} aria-hidden>
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        {NEURON_INPUT_Y.map((y, index) => {
          const weight = NEURON.weights[index] ?? 0;
          return (
            <path
              key={y}
              d={`M 22 ${y} C 44 ${y}, 46 45, 58 45`}
              stroke={weight >= 0 ? '#D0605A' : '#6C8EB8'}
              strokeWidth={1.5 + Math.abs(weight) * 1.4}
              strokeLinecap="round"
              fill="none"
              opacity={inputs[index] === 1 ? 0.9 : 0.3}
              style={{transition: 'opacity 0.25s'}}
            />
          );
        })}
        {NEURON_INPUT_Y.map((y, index) => (
          <circle
            key={y}
            cx={14}
            cy={y}
            r={8}
            fill={inputs[index] === 1 ? FELT.gold : FELT.cream}
            stroke={FELT.creamDark}
            strokeWidth={2}
            filter={`url(#${ids.fuzz})`}
            style={{transition: 'fill 0.25s'}}
          />
        ))}
        {fires && (
          <circle cx={74} cy={45} r={26} fill={FELT.gold} opacity={0.25} />
        )}
        <circle
          cx={74}
          cy={45}
          r={18}
          fill={fires ? FELT.cardinal : FELT.cream}
          stroke={FELT.creamDark}
          strokeWidth={2.5}
          filter={`url(#${ids.fuzz})`}
          style={{transition: 'fill 0.25s'}}
        />
        {[
          [-5, -5],
          [5, -5],
          [-5, 5],
          [5, 5],
        ].map(([dx, dy]) => (
          <circle
            key={`${dx}${dy}`}
            cx={74 + (dx ?? 0)}
            cy={45 + (dy ?? 0)}
            r={2}
            fill={FELT.ink}
            opacity={0.5}
          />
        ))}
        {fires && (
          <Hop key={pattern}>
            <path
              d="M 74 12 L 77 20 L 85 21 L 79 26 L 81 34 L 74 29 L 67 34 L 69 26 L 63 21 L 71 20 Z"
              fill={FELT.gold}
              transform="translate(92 18) scale(0.55) translate(-74 -23)"
            />
          </Hop>
        )}
      </svg>
    </ToyShell>
  );
}

/** Loss-bowl geometry, in the toy's 100×80 viewBox. */
const BOWL = {min: 50, left: 8, right: 92, bottom: 68, curve: 0.026} as const;
/** Fraction of the way to the minimum each click steps (learning rate × slope). */
const STEP_FRACTION = 0.55;
/** Within this distance of the minimum counts as converged. */
const CONVERGED = 1;

/** The bowl's surface height at `x`; SVG y grows downward, so the minimum is the largest y. */
function bowlY(x: number): number {
  return BOWL.bottom - (x - BOWL.min) ** 2 * BOWL.curve;
}

const Rolling = styled.g`
  transition: transform 0.45s cubic-bezier(0.34, 1.4, 0.64, 1);
`;

/**
 * Gradient descent on a loss bowl: each click moves the pom-pom one step
 * downhill, in proportion to the slope, so steps shrink near the bottom. At
 * the minimum it celebrates; the next click starts over from a random side.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyGradientDescent({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [x, setX] = useState<number>(BOWL.left + 4);
  const [trail, setTrail] = useState<number[]>([]);
  const isDone = Math.abs(x - BOWL.min) < CONVERGED;
  const loss = ((x - BOWL.min) / (BOWL.min - BOWL.left)) ** 2;
  const bowl = Array.from({length: 22}, (_, index) => {
    const px = BOWL.left + (index / 21) * (BOWL.right - BOWL.left);
    return `${px.toFixed(1)},${bowlY(px).toFixed(1)}`;
  }).join(' ');

  const step = () => {
    if (isDone) {
      const start = Math.random() < 0.5 ? BOWL.left + 3 : BOWL.right - 3;
      setX(start + (Math.random() - 0.5) * 6);
      setTrail([]);
      return;
    }
    setTrail([...trail, x].slice(-6));
    setX(x - STEP_FRACTION * (x - BOWL.min));
  };

  return (
    <ToyShell
      isPreview={isPreview}
      label="Gradient descent"
      caption={isDone ? 'minimum! 🎉' : `loss ${loss.toFixed(2)}`}
      onPoke={step}
    >
      <svg viewBox="0 0 100 80" width={size} height={size * 0.8} aria-hidden>
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <polygon
          points={`${bowl} ${BOWL.right},76 ${BOWL.left},76`}
          fill="#F4C9C4"
          filter={`url(#${ids.fuzz})`}
        />
        <polyline
          points={bowl}
          fill="none"
          stroke={FELT.cardinal}
          strokeWidth={2}
          strokeDasharray="4 3"
          strokeLinecap="round"
        />
        {trail.map((px, index) => (
          <circle
            // A trail step is identified by its order.
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={index}
            cx={px}
            cy={bowlY(px) - 6}
            r={1.8}
            fill={FELT.ink}
            opacity={0.15 + (index / trail.length) * 0.35}
          />
        ))}
        <Rolling style={{transform: `translate(${x}px, ${bowlY(x) - 7}px)`}}>
          <circle r={7} fill={FELT.blue} stroke="#fff" strokeWidth={1.2} />
          <circle cx={-2.3} cy={-0.8} r={1.1} fill={FELT.ink} />
          <circle cx={2.3} cy={-0.8} r={1.1} fill={FELT.ink} />
          {isDone && (
            <path
              d="M -2.5 2 Q 0 4.5 2.5 2"
              stroke={FELT.ink}
              strokeWidth={1}
              fill="none"
              strokeLinecap="round"
            />
          )}
        </Rolling>
      </svg>
    </ToyShell>
  );
}

/** Temperatures the toy cycles through, with their captions. */
const TEMPERATURES = [
  {t: 0.35, label: 'careful'},
  {t: 1, label: 'balanced'},
  {t: 2.5, label: 'wild'},
] as const;
const TEMPERATURE_LOGITS = [2.4, 1.4, 0.9, 0] as const;

const GummyFill = styled.rect`
  transform-box: fill-box;
  transform-origin: left center;
  transition: transform 0.4s cubic-bezier(0.34, 1.4, 0.64, 1);
`;

/**
 * Softmax temperature: four gummy bars show a model's word probabilities;
 * each click turns the thermometer up a notch (wrapping back to cold) and
 * the bars sharpen or flatten.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyTemperature({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [level, setLevel] = useState(1);
  const setting = TEMPERATURES[level] ?? TEMPERATURES[1];
  const weights = TEMPERATURE_LOGITS.map(logit => Math.exp(logit / setting.t));
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  const shares = weights.map(weight => weight / total);
  const mercury = 12 + (level / (TEMPERATURES.length - 1)) * 40;

  return (
    <ToyShell
      isPreview={isPreview}
      label="Temperature"
      caption={`T ${setting.t} · ${setting.label}`}
      onPoke={() => {
        setLevel((level + 1) % TEMPERATURES.length);
      }}
    >
      <svg viewBox="0 0 100 80" width={size} height={size * 0.8} aria-hidden>
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        {/* Thermometer. */}
        <rect
          x={6}
          y={8}
          width={10}
          height={58}
          rx={5}
          fill="#fff"
          stroke={FELT.creamDark}
          strokeWidth={2}
        />
        <rect
          x={8.5}
          y={66 - mercury}
          width={5}
          height={mercury}
          rx={2.5}
          fill={FELT.cardinal}
          style={{transition: 'y 0.4s, height 0.4s'}}
        />
        <circle
          cx={11}
          cy={69}
          r={7}
          fill={FELT.cardinal}
          stroke={FELT.creamDark}
          strokeWidth={2}
        />
        {shares.map((share, index) => {
          const y = 10 + index * 16;
          return (
            // Bars are fixed rows.
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <g key={index}>
              <rect
                x={26}
                y={y}
                width={68}
                height={11}
                rx={5.5}
                fill={FELT.cream}
              />
              <GummyFill
                x={26}
                y={y}
                width={68}
                height={11}
                rx={5.5}
                fill={CANDY[index] ?? FELT.cardinal}
                filter={`url(#${ids.fuzz})`}
                style={{transform: `scaleX(${Math.max(0.06, share)})`}}
              />
            </g>
          );
        })}
      </svg>
    </ToyShell>
  );
}

/** One classifier example: the true animal and the model's guess. */
interface Example {
  animal: 'cat' | 'dog';
  guess: 'cat' | 'dog';
  confidence: number;
}

/** How often the toy model guesses right. */
const ACCURACY = 0.85;

function nextExample(): Example {
  const animal = Math.random() < 0.5 ? 'cat' : 'dog';
  const isRight = Math.random() < ACCURACY;
  const other = animal === 'cat' ? 'dog' : 'cat';
  return {
    animal,
    guess: isRight ? animal : other,
    confidence: isRight
      ? 0.7 + Math.random() * 0.29
      : 0.5 + Math.random() * 0.2,
  };
}

/** A felt cat face centered at (50, 40). */
function CatFace() {
  return (
    <g>
      <path
        d="M 34 30 L 36 14 L 46 25 Z M 66 30 L 64 14 L 54 25 Z"
        fill="#E0A060"
      />
      <circle cx={50} cy={40} r={18} fill="#E0A060" />
      <circle cx={43} cy={37} r={2.4} fill={FELT.ink} />
      <circle cx={57} cy={37} r={2.4} fill={FELT.ink} />
      <path d="M 48 44 L 52 44 L 50 46.5 Z" fill="#E97C8A" />
      <path
        d="M 30 44 H 42 M 30 48 L 42 46 M 58 44 H 70 M 58 46 L 70 48"
        stroke={FELT.ink}
        strokeWidth={0.8}
        opacity={0.6}
      />
    </g>
  );
}

/** A felt dog face centered at (50, 40). */
function DogFace() {
  return (
    <g>
      <circle cx={50} cy={40} r={18} fill="#C9A077" />
      <ellipse
        cx={33}
        cy={40}
        rx={6}
        ry={13}
        fill="#7E5638"
        transform="rotate(15 33 40)"
      />
      <ellipse
        cx={67}
        cy={40}
        rx={6}
        ry={13}
        fill="#7E5638"
        transform="rotate(-15 67 40)"
      />
      <ellipse cx={50} cy={47} rx={9} ry={7} fill="#EBD6BC" />
      <circle cx={43} cy={35} r={2.4} fill={FELT.ink} />
      <circle cx={57} cy={35} r={2.4} fill={FELT.ink} />
      <ellipse cx={50} cy={44} rx={3.2} ry={2.4} fill={FELT.ink} />
    </g>
  );
}

/**
 * A cat-or-dog classifier: click to show a new felt photo; the model guesses
 * with a confidence, and it is sometimes wrong (and less sure when it is).
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyClassifier({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [examples, setExamples] = useState<Example[]>([]);
  const example = examples[examples.length - 1];
  const correct = examples.filter(item => item.guess === item.animal).length;
  const isRight = example !== undefined && example.guess === example.animal;

  return (
    <ToyShell
      isPreview={isPreview}
      label="Cat or dog classifier"
      caption={
        example === undefined
          ? 'cat or dog?'
          : `guessed ${example.guess} at ${Math.round(example.confidence * 100)}%, ${
              isRight ? 'right' : 'wrong'
            }; ${correct} of ${examples.length} right`
      }
      onPoke={() => {
        setExamples([...examples, nextExample()]);
      }}
    >
      <svg viewBox="0 0 100 90" width={size} height={size * 0.9} aria-hidden>
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <rect
          x={18}
          y={10}
          width={64}
          height={62}
          rx={8}
          fill="#fff"
          stroke={FELT.creamDark}
          strokeWidth={2.5}
          filter={`url(#${ids.fuzz})`}
          transform="rotate(-3 50 41)"
        />
        <rect
          x={24}
          y={16}
          width={52}
          height={50}
          rx={4}
          fill="#DCEFF7"
          transform="rotate(-3 50 41)"
        />
        <Hop key={examples.length}>
          <g transform="rotate(-3 50 41)">
            {(example?.animal ?? 'cat') === 'cat' ? <CatFace /> : <DogFace />}
          </g>
        </Hop>
        {example !== undefined && (
          <Hop key={`badge-${examples.length}`}>
            {/* The model's guess, ringed green when right and red when wrong. */}
            <circle
              cx={82}
              cy={16}
              r={13}
              fill="#fff"
              stroke={isRight ? FELT.mint : FELT.cardinal}
              strokeWidth={3}
            />
            <g transform="translate(82 17) scale(0.5) translate(-50 -40)">
              {example.guess === 'cat' ? <CatFace /> : <DogFace />}
            </g>
          </Hop>
        )}
      </svg>
    </ToyShell>
  );
}
